import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";

export type InlineConfirmProps = {
  /** 支援技術向けの名前（「モデルの削除」など）。 */
  readonly label: string;
  readonly children: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /** 削除だけ true。赤は最後に押す 1 か所だけに使う。 */
  readonly destructive?: boolean;
  /** 確定に必要な条件（ファイルを選んだか等）。省略時は常に押せる。 */
  readonly canConfirm?: boolean;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
};

/**
 * 元に戻せない操作の確認を、押した場所のすぐ近くに開く。
 *
 * ダイアログにしないのは自社フロントの作法に合わせるため（Secrets の編集や
 * フォーム定義の追加も、ダイアログを使わずページ内に開く）。失敗しても閉じず、
 * 理由をこの中に出す。閉じると理由を読み落とすため。処理中は「やめる」も押せない
 * （閉じると、途中で失敗したときの理由を出す場所が無くなる）。
 */
export function InlineConfirm({
  label,
  children,
  confirmLabel,
  cancelLabel,
  destructive = false,
  canConfirm = true,
  isPending,
  error,
  onConfirm,
  onCancel,
}: InlineConfirmProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className="border-border bg-background flex flex-col gap-2 rounded-md border p-3 text-sm"
    >
      {children}
      {error === null ? null : (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={destructive ? "destructive" : "default"}
          disabled={!canConfirm || isPending}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
}
